import { useContext } from "react";

import { UserContext } from "../../../../core/security/UserProvider";
import { CreateNoteRequest } from "../../../api/master-api/notes/CreateNoteRequest";
import { CreateNotesRequest } from "../../../api/master-api/notes/CreateNotesRequest";
import { DeleteNoteRequest } from "../../../api/master-api/notes/DeleteNoteRequest";
import { GetNotesBySubjectIdsRequest } from "../../../api/master-api/notes/GetNotesBySubjectIdsRequest";
import { mapToNoteItem } from "../../../api/master-api/notes/GetNotesBySubjectIdsResponse";
import { useNotesApi } from "../../../api/master-api/notes/useNotesApi";
import { NoteSubjectType } from "../../../types/NoteSubjectType";
import { getMiliseconds } from "../../../utils/getMiliseconds";
import { NoteItem } from "./state/NoteItem";

export const useNotesActions = () => {
    const notesApi = useNotesApi();
    const userContext = useContext(UserContext);

    const createNote = async (subjectId: string, content: string, subjectType: NoteSubjectType): Promise<NoteItem> => {
        const request: CreateNoteRequest = {
            subjectId,
            subjectType,
            content,
            authorEmail: userContext.email,
            authorName: userContext.name
        };

        const response = await notesApi.createNote(request);

        return {
            id: response.id,
            author: request.authorName,
            creationDate: new Date().toISOString(),
            content,
            subjectId
        };
    };

    const createNotes = async (
        items: { subjectId: string; content: string; subjectType: NoteSubjectType }[]
    ): Promise<void> => {
        const request: CreateNotesRequest = {
            items: items.map((item) => ({
                subjectType: item.subjectType,
                subjectId: item.subjectId,
                content: item.content,
                authorEmail: userContext.email,
                authorName: userContext.name
            }))
        };

        await notesApi.createNotes(request);
    };

    const deleteNote = async (subjectId: string, id: string, subjectType: NoteSubjectType): Promise<void> => {
        const request: DeleteNoteRequest = { id, subjectId, subjectType };
        await notesApi.deleteNote(request);
    };

    const getNotes = async (sourceIds: string[]): Promise<NoteItem[]> => {
        const request: GetNotesBySubjectIdsRequest = { subjectIds: sourceIds };
        const response = await notesApi.getNotesBySubjectIds(request);

        let notes = Object.entries(response)
            .map(([key, values]) => values.map((item) => mapToNoteItem(item, key)))
            .flat();

        notes = sortNotesDescending(notes);

        return notes;
    };

    const checkIfNoteBelongsToUser = (note: NoteItem): boolean => {
        return note.author === userContext.name;
    };

    const checkIfNoteBelongsToSubject = (note: NoteItem, subjectId: string): boolean => {
        return note.subjectId === subjectId;
    };

    return { getNotes, createNote, createNotes, deleteNote, checkIfNoteBelongsToUser, checkIfNoteBelongsToSubject };
};

export const sortNotesDescending = (notes: NoteItem[]): NoteItem[] => {
    return notes.sort((a: NoteItem, b: NoteItem) => getMiliseconds(b.creationDate) - getMiliseconds(a.creationDate));
};
